import React, { useState, useCallback, useEffect, useMemo } from 'react'
import MapContainer from '../components/map/MapContainer'
import {
  Box,
  Button,
  Collapse,
  IconButton,
  useTheme,
  Typography,
} from '@mui/material'
import PageHeaderWithIcons from '@/components/heder/PageHeaderWithIcons'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { getConsumers, ConsumerRow } from '@/services/consumerService'
import { useTableStore } from '@/store/table/table'
import { useAppTheme } from '@/theme/ThemeContext'
import { buildConsumerColumns } from '@/utils/consumerColumns'
import { getTablePageSize } from '@/hooks/useTablePageSize'
import SendNotificationDialog from '@/components/notification-send/SendNotificationDialog'
import { useSearch } from '@/hooks/useSearch'
import { debugLog } from '@/utils/debugLog'

const TABLE_MAX_HEIGHT = 'calc(69.2vh - 240px)'

// The meters panel at its tallest: the grid (pagination included) at
// TABLE_MAX_HEIGHT plus the panel's count bar.
const tablePanelMaxHeight = () => window.innerHeight * 0.692 - 240 + 52

const Map: React.FC<{ sidebarWidth: number }> = ({ sidebarWidth }) => {
  const theme = useTheme()
  const { langKey } = useAppTheme()
  const [expanded, setExpanded] = useState(false)

  const {
    setFilteredRowsStore,
    setColumnsStore,
    apiFilters,
    clearApiFilters,
    filteredRowsStore,
  } = useTableStore()

  // Table data state
  const [rows, setRows] = useState<ConsumerRow[]>([])
  const [resourceNames, setResourceNames] = useState<string[]>([])
  const [resourceHeaders, setResourceHeaders] = useState<Record<string, string>>({})
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(() => getTablePageSize(25))

  // Polygon filter state
  const [polygonVertices, setPolygonVertices] = useState<string[] | null>(null)
  const [sendOpen, setSendOpen] = useState(false)

  // The text waits for a pause, a newer request stops the older one, and only the newest answer lands.
  // A new search starts from the first page in the same tick, so it sends one table request.
  const search = useSearch({
    initial: filteredRowsStore.searchFilter?.value?.trim() ?? '',
    onQueryChange: () => setPage(0),
  })
  const debouncedSearch = search.query || undefined
  const { run: runSearch, searchNow } = search

  // Build dynamic columns from API data (same as Consumers table)
  const dynamicColumns = useMemo(() =>
    buildConsumerColumns({ rows, resourceNames, resourceHeaders, langKey }),
    [resourceNames, rows, resourceHeaders, langKey]
  )

  // Keep the advanced-filters modal populated even while the collapsed table is unmounted.
  useEffect(() => {
    setColumnsStore(dynamicColumns)
  }, [dynamicColumns, setColumnsStore])

  // Build combined filters: polygon + apiFilters + search
  const buildAllFilters = useCallback(() => {
    const filters = [...apiFilters]
    if (polygonVertices) {
      filters.push({ name: 'polygon', value: polygonVertices })
    }
    if (debouncedSearch) {
      filters.push({ name: 'search', value: [debouncedSearch] })
    }
    return filters
  }, [apiFilters, polygonVertices, debouncedSearch])

  // Fetch consumers (filtered by polygon + apiFilters + search)
  const fetchConsumersData = useCallback(
    (pageNum: number, size: number) => {
      const accountId = localStorage.getItem('accountId') || '46'
      const filters = buildAllFilters()
      runSearch(
        signal =>
          getConsumers(
            {
              accountId: Number(accountId),
              page: pageNum + 1, // API is 1-indexed
              size,
              langKey,
            },
            filters,
            signal
          ),
        result => {
          setRows(result.rows)
          setTotalRows(result.total)
          setResourceNames(result.resourceNames)
          setResourceHeaders(result.resourceHeaders)
        },
        err => {
          console.error('Failed to fetch map consumers:', err)
          setRows([])
          setTotalRows(0)
        }
      )
    },
    [langKey, buildAllFilters, runSearch]
  )

  // Refetch when filters, search, or page change
  useEffect(() => {
    fetchConsumersData(page, pageSize)
  }, [page, pageSize, apiFilters, debouncedSearch, polygonVertices, langKey])

  // Reset page when filters change. A search resets it through the hook.
  useEffect(() => {
    setPage(0)
  }, [apiFilters, polygonVertices])

  // Called when user finishes drawing a polygon
  const handlePolygonComplete = useCallback((vertices: string[]) => {
    debugLog('🔷 Polygon drawn with', vertices.length, 'vertices')
    setPolygonVertices(vertices)
    setExpanded(true) // Auto-expand table
  }, [])

  // Called when user clears the polygon drawing
  const handlePolygonClear = useCallback(() => {
    debugLog('🔷 Polygon cleared')
    setPolygonVertices(null)
  }, [])

  // MapContainer emits each vertex as "lng,lat" (PostGIS convention, longitude
  // first — see polygonToVertices in src/components/map/usePolygonDraw.ts).
  const parseVertices = (raw: string[]) =>
    raw.map(v => {
      const [longitude, latitude] = v.split(',').map(Number)
      return { longitude, latitude }
    })

  // Distinct name (not `accountId`) to avoid shadowing the per-function
  // `accountId` local used in fetchConsumersData above (that read-path
  // fallback is pre-existing and untouched here). Guarded like the
  // pattern in src/pages/Consumers.tsx: read the raw value first so a
  // missing/invalid accountId becomes NaN instead of a silent 0
  // (Number(null) === 0) that could reach the dialog — and a real
  // billable send — as if it were a real account id.
  const notificationAccountIdRaw = localStorage.getItem('accountId')
  const notificationAccountId = notificationAccountIdRaw
    ? Number(notificationAccountIdRaw)
    : NaN

  // Handle pagination change
  const handlePaginationChange = useCallback((newPage: number, newPageSize: number) => {
    setPage(newPage)
    setPageSize(newPageSize)
  }, [])

  // Clear all filters (polygon + api filters + search)
  const handleGlobalClear = useCallback(() => {
    clearApiFilters()
    setFilteredRowsStore({})
    setPolygonVertices(null)
    setPage(0)
    searchNow('')
  }, [clearApiFilters, setFilteredRowsStore, searchNow])

  // The header reads its text back from the store, so the store holds what was typed.
  const rememberSearch = (value: string) =>
    setFilteredRowsStore(
      value.trim() ? { searchFilter: { label: 'חיפוש', value } } : {}
    )

  const changeSearchText = (value: string) => {
    rememberSearch(value)
    search.setText(value)
  }

  const executeSearch = (value: string) => {
    rememberSearch(value)
    search.searchNow(value)
  }

  // Build filters for map markers (exclude polygon — that's drawn on the map itself)
  const mapFilters = useMemo(() => {
    const filters = [...apiFilters]
    if (debouncedSearch) {
      filters.push({ name: 'search', value: [debouncedSearch] })
    }
    return filters
  }, [apiFilters, debouncedSearch])

  const hasPolygonData = polygonVertices !== null
  const hasFilters = apiFilters.length > 0 || debouncedSearch

  return (
    <Box
      height="100%"
      width="100%"
      sx={{
        backgroundColor: theme.palette.background.default,
      }}
    >
      <PageHeaderWithIcons
        title="תצוגה על גבי מפה"
        showSearch
        proSearch
        executeSearch={executeSearch}
        onSearchChange={changeSearchText}
        onClearFilters={handleGlobalClear}
      />
      <MapContainer
        fullPage={true}
        polygon={polygonVertices}
        onPolygonComplete={handlePolygonComplete}
        onPolygonClear={handlePolygonClear}
        onDrawingStart={() => setExpanded(false)}
        bottomOverlayHeight={tablePanelMaxHeight}
        filters={mapFilters}
      />
      <Box
        position="absolute"
        bottom={0}
        right={`${sidebarWidth + 100}px`}
        left={`100px`}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
        }}
      >
        {polygonVertices && polygonVertices.length >= 3 && (
          <Box position="absolute" top={8} left={16} sx={{ direction: 'rtl' }}>
            <Button
              variant="contained"
              size="small"
              disabled={!notificationAccountId}
              onClick={() => setSendOpen(true)}
            >
              שליחת הודעה לאזור המסומן
            </Button>
          </Box>
        )}
        <Box>
          <Box width="fit-content" margin="0 auto" display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => setExpanded(!expanded)}>
              {expanded ? (
                <ExpandMoreIcon fontSize="large" />
              ) : (
                <ExpandLessIcon fontSize="large" />
              )}
            </IconButton>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontFamily: 'Rubik, sans-serif',
              }}
            >
              {search.isSearching
                ? 'טוען נתונים...'
                : hasPolygonData || hasFilters
                  ? `${totalRows} צרכנים מסוננים`
                  : `${totalRows} צרכנים סה"כ`}
            </Typography>
          </Box>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <DataGridPremiumDemo
              rows={rows}
              columns={dynamicColumns}
              hideFooter={true}
              headerPosition="top"
              pageSize={pageSize}
              serverSideRowCount={totalRows}
              serverSidePage={page}
              onPaginationChange={handlePaginationChange}
              paginationMode="server"
              filterMode="server"
              loading={search.isSearching}
              maxHeight={TABLE_MAX_HEIGHT}
            />
          </Collapse>
        </Box>
      </Box>
      <SendNotificationDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        accountId={notificationAccountId}
        audience={{
          kind: 'area',
          vertices: parseVertices(polygonVertices || []),
          filters: mapFilters,
        }}
      />
    </Box>
  )
}

export default Map
